import { makeAutoObservable } from 'mobx';
import path from 'path';
import { IconName } from '../../../../../components/display/Icon';
import { formatTime } from '../../../../../utils/date';
import { formatSizeBinary } from '../../../../../utils/format';
import S3Files from './S3Files';

const ARCHIVE_REGEXP = /\.(rar|zip|r\d\d|z\d\d)/;

const VOLUME_NAMING_RULES = [
  {
    match: /\.part(\d+)\.rar$/,
    replace(_: unknown, partNumber: string) {
      const firstPartNumber = '1'.padStart(partNumber.length, '0');
      return `.part${firstPartNumber}.rar`;
    },
  },
  { match: /\.r\d\d$/, replace: '.rar' },
  { match: /\.z\d\d$/, replace: '.zip' },
  { match: /\.\d{3}$/, replace: '.001' },
];

export default class S3File {
  lastModified!: string;
  name!: string;
  size?: number;
  url!: string;

  isSelected = false;

  constructor(
    dto: S3FileDto,
    private readonly fileList?: S3Files,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    Object.assign(this, dto);
  }

  static fromUrl(url: string) {
    if (typeof url !== 'string') {
      throw new TypeError('Invalid URL');
    }

    return new S3File({
      name: path.basename(url),
      url,
    });
  }

  get id() {
    return this.url;
  }

  get extension() {
    return path.extname(this.url);
  }

  get isArchive(): boolean {
    return ARCHIVE_REGEXP.test(this.extension);
  }

  get icon(): IconName {
    if (this.isDir) {
      return 'folder';
    }

    if (this.isArchive) {
      return 'file-archive';
    }

    return 'file';
  }

  get isSelectable() {
    return this.isArchive;
  }

  get isDir(): boolean {
    return this.url.endsWith('/');
  }

  get displaySize(): string {
    return this.isDir || !this.size ? '' : formatSizeBinary(this.size);
  }

  get displayModified(): string {
    return this.lastModified ? formatTime(this.lastModified) : '';
  }

  get firstVolumeName(): string {
    return getFirstVolumeFileName(this.name);
  }

  setSelected(selected: boolean, fireListEvent = true) {
    this.isSelected = this.isSelectable && selected;
    if (fireListEvent) {
      this.fileList?.onFileToggled(this);
    }
  }

  toggleSelection() {
    this.isSelected = !this.isSelected;
  }

  toJSON() {
    return {
      url: this.url,
    };
  }
}

export function getFirstVolumeFileName(archiveFileName: string): string {
  let name = archiveFileName.toLowerCase();

  for (const { match, replace } of VOLUME_NAMING_RULES) {
    name = name.replace(match, replace as any);
  }

  return name;
}

export interface S3FileDto {
  isSelected?: boolean;
  modified?: string;
  name: string;
  size?: number;
  url?: string;
}
